import { Box, Divider, HStack } from "@chakra-ui/layout";
import {
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type GlobalCreditAccountFragment,
  GlobalCreditAccountOrderByColumnEnum,
} from "@src/__generated__/graphql";
import { CreditAccountsTypeLinks } from "@src/components/modules/credit-account/global-accounts-listing/components/credit-accounts-type-links";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  ColoredAmount,
  DataTable,
  Link,
  LoadingOverlay,
  MenuItemLink,
  ModalConfirm,
  NavBar,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { currency } from "@src/utils/formatters";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useMemo } from "react";
import { GlobalCreditAccountModal } from "./components/modal";
import { GlobalAccountsListingStore } from "./store";

export const PageGlobalCreditAccounts = observer(
  function PageGlobalCreditAccounts() {
    const appStore = useStore();
    const store = useLocalObservable(
      () => new GlobalAccountsListingStore(appStore),
    );

    useUpdateURI(store.searchParams);

    const columnsDefinition = useMemo<
      BodyColumnDef<
        GlobalCreditAccountFragment,
        GlobalCreditAccountOrderByColumnEnum
      >[]
    >(
      () => [
        {
          id: "id",
          meta: { label: t`ID` },
          header: t`ID`,
          orderBy: GlobalCreditAccountOrderByColumnEnum.Id,
          cell: ({ row }) => (
            <Link
              href={{
                pathname: "/credit-accounts/global-detail",
                query: {
                  id: row.original.id,
                },
              }}
            >
              {row.original.id}
            </Link>
          ),
        },
        {
          id: "title",
          meta: { label: t`Title` },
          header: t`Title`,
          orderBy: GlobalCreditAccountOrderByColumnEnum.Title,
          cell: ({ row }) => (
            <Link
              href={{
                pathname: "/credit-accounts/global-detail",
                query: {
                  id: row.original.id,
                },
              }}
            >
              {row.original.title}
            </Link>
          ),
        },
        {
          id: "total_credit",
          meta: { label: t`Total Credit` },
          header: t`Total Credit`,
          cell: ({ row }) => (
            <ColoredAmount value={row.original.stats.total_credit}>
              {currency.formatByWorkspace(row.original.stats.total_credit)}
            </ColoredAmount>
          ),
        },
        {
          id: "total_debit",
          meta: { label: t`Total Debit` },
          header: t`Total Debit`,
          cell: ({ row }) => (
            <ColoredAmount value={row.original.stats.total_debit}>
              {currency.formatByWorkspace(row.original.stats.total_debit)}
            </ColoredAmount>
          ),
        },
        {
          id: "remaining_credit",
          meta: { label: t`Remaining Credit` },
          header: t`Remaining Credit`,
          cell: ({ row }) => (
            <ColoredAmount value={row.original.stats.remaining_credit}>
              <b>
                {currency.formatByWorkspace(
                  row.original.stats.remaining_credit,
                )}
              </b>
            </ColoredAmount>
          ),
        },
        {
          id: ACTIONS_COLUMN_ID,
          meta: { label: t`Actions` },
          header: t`Actions`,
          cell: ({ row }) => {
            const creditAccount = row.original;

            return (
              <Menu>
                <MenuButton
                  as={IconButton}
                  aria-label={t`Actions`}
                  colorScheme="gray"
                  icon={<Icon name="dots-vertical" />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItemLink
                    href={{
                      pathname: "/credit-accounts/global-detail",
                      query: { id: creditAccount.id },
                    }}
                  >
                    <Trans>Detail</Trans>
                  </MenuItemLink>
                  <MenuItem
                    onClick={() => store.handleEditCreditAccount(creditAccount)}
                  >
                    <Trans>Edit</Trans>
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      store.handleDeleteCreditAccount(creditAccount.id)
                    }
                  >
                    <Trans>Delete</Trans>
                  </MenuItem>
                </MenuList>
              </Menu>
            );
          },
        },
      ],
      [store],
    );

    return (
      <DataTable>
        <Box>
          <DataTable.NavBar>
            <HStack justify="space-between" w="full">
              <NavBar fullWidth>
                <CreditAccountsTypeLinks />
              </NavBar>
              <Box>
                <Button
                  leftIcon={<Icon name="plus" />}
                  onClick={store.handleCreateCreditAccount}
                >
                  <Trans>Add Credit Account</Trans>
                </Button>
              </Box>
            </HStack>
          </DataTable.NavBar>

          <Divider />

          <DataTable.NavBar>
            <Box py="4">
              <FiltersPanelRoot filters={store.where}>
                <FiltersPanelSearch onSearch={store.handleChangeSearch} />
              </FiltersPanelRoot>
            </Box>
          </DataTable.NavBar>
        </Box>

        <Box position="relative">
          <LoadingOverlay
            isLoading={store.isFetching}
            position="absolute"
            size="lg"
          >
            <DataTable.Body
              tableKey={store.moduleKey}
              data={store.data}
              columns={columnsDefinition}
              orderedBy={store.activeOrderBy}
              onOrderByChange={store.handleChangeOrderBy}
            />
          </LoadingOverlay>
        </Box>

        <Pagination store={store.pagination} />

        <GlobalCreditAccountModal
          store={store.modalStore}
          isLoading={store.isModalLoading}
        />

        <ModalConfirm
          isLoading={store.isModalLoading}
          isOpen={store.deleteModalDisclosure.isOpen}
          onClose={store.deleteModalDisclosure.onClose}
          onConfirm={store.handleDeleteModalConfirm}
          destructive
          heading={t`Delete credit account`}
          message={t`Are you sure you want to delete this credit account?`}
          closeOnConfirm={false}
        />
      </DataTable>
    );
  },
);
